import { TimelineLite } from "gsap";
import { VOICEOVER_ENDED, VOICEOVER_STARTED } from "html-living-framework/lib/Data/Topics";
import { floatBetween } from "html-living-framework/lib/Utils/Random";
import {Container} from "../core/Container";

export abstract class AbstractCharacter extends Container {

    public abstract spine: PIXI.spine.Spine;
    protected blinkTimeline = new TimelineLite();

    protected _subscriptions: any[] = [];

    protected abstract animations: {
        BLINK: string,
        IDLE: string,
        LIP_FLAP: string,
        WALK: string,
        WAVE: string,
    };

    protected tracks = {
        FEET: 0,
        ARMS: 1,
        HEAD: 2,
        MOUTH: 3,
        EYES: 4,
    };

    public constructor(pCharacterName: string) {
        super();
        this.name = pCharacterName;
    }

    public destroy() {
        super.destroy();
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.blinkTimeline.kill();
    }

    public onLayout() {
        this._subscriptions.push(PubSub.subscribe(VOICEOVER_STARTED, this.onVoiceoverStarted.bind(this)));
        this._subscriptions.push(PubSub.subscribe(VOICEOVER_ENDED, this.onVoiceoverEnded.bind(this)));

        this.spine.state.addListener({complete: this.onAnimationComplete.bind(this)});

        this.spine.stateData.setMix(this.animations.IDLE, this.animations.WALK, 0.25);
        this.spine.stateData.setMix(this.animations.WALK, this.animations.IDLE, 0.25);

        this.scale.set(Math.max(this.scale.x, this.scale.y)); // preserve aspect ratio

        this.spine.state.setAnimation(this.tracks.FEET, this.animations.IDLE, true);

        this.blinkTimeline.call(() => {
            this.spine.state.setAnimation(this.tracks.EYES, this.animations.BLINK, false);
            this.blinkTimeline.delay(floatBetween(1, 5));
            this.blinkTimeline.restart(true);
        });
        this.blinkTimeline.delay(floatBetween(1, 5));
    }

    public update(deltaTime: number) {
        this.spine.update(deltaTime);
    }

    /**
     * speed: pixels per second
     */
    public walkTo(x: number, speed: number, ease: gsap.Ease, callback: () => void, animationSpeed: number = 1) {
        const entry = this.spine.state.setAnimation(this.tracks.FEET, this.animations.WALK, true);
        if (x < this.x) {
            this.spine.scale.x = Math.abs(this.spine.scale.x) * -1;
        }
        else {
            this.spine.scale.x = Math.abs(this.spine.scale.x);
        }

        const distance = Math.abs(x - this.x);
        const duration = distance / speed;

        entry.timeScale = animationSpeed;
        this.moveXTo(x, duration, ease, () => {
            this.spine.state.setAnimation(this.tracks.FEET, this.animations.IDLE, true);
            callback();
        });
    }

    public Wave() {
        this.spine.state.setEmptyAnimation(this.tracks.ARMS, 0);
        const entry = this.spine.state.addAnimation(this.tracks.ARMS, this.animations.WAVE, false, 0);
        entry.mixDuration = 0.25;
    }

    protected onAnimationComplete(pEntry: PIXI.spine.core.TrackEntry) {
        switch (pEntry.animation.name) {
            case this.animations.WAVE:
                this.spine.state.setEmptyAnimation(this.tracks.ARMS, 0.25);
                break;
        }
    }

    protected onVoiceoverStarted(pTopic: string, pData: string) {
        if (pData.startsWith(this.name)) {
            this.spine.state.setAnimation(this.tracks.MOUTH, this.animations.LIP_FLAP, true);
        }
    }

    protected onVoiceoverEnded(pTopic: string, pData: any) {
        if (pData.startsWith(this.name)) {
            this.spine.state.setEmptyAnimation(this.tracks.MOUTH, 0);
        }
    }

}
